import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  SvgIcon,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Facebook, Google, Instagram, LinkedIn, YouTube } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { stringToSlug } from 'utils/slugify';
import PageSectionContainer from 'components/page/PageSectionContainer';

import { XIcon } from './components/XIcon';
import { XingIcon } from './components';

const PREFIX = 'Footer';

const classes = {
  center: `${PREFIX}-center`,
};

const Root = styled('footer')({
  [`& .${classes.center}`]: {
    textAlign: 'center',
  },
});

interface Social {
  title: string;
  link: string;
  icon: ReactNode;
}

const slugOptions = { lower: true, strict: true };
const currentYear = new Date().getFullYear();

export const Footer = () => {
  const { t, i18n } = useTranslation();

  const onMobileAndTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const shortcuts = [
    {
      name: t('footer.shortcut.1'),
      path: `/articles/${stringToSlug(t('footer.shortcut.1'), slugOptions)}`,
    },
    {
      name: t('footer.shortcut.lab'),
      path: '/lab',
    },
    {
      name: t('footer.shortcut.2'),
      path: `/articles/${stringToSlug(t('footer.shortcut.2'), slugOptions)}`,
    },
    {
      name: t('footer.shortcut.3'),
      path: `/articles/${stringToSlug(t('footer.shortcut.3'), slugOptions)}`,
    },
    {
      name: t('service-center.software-updates.title'),
      path: '/service-center/updates',
    },
  ];

  const socials: Social[] = [
    {
      title: 'Facebook',
      link: `https://www.facebook.com/${t('footer.social.facebook')}`,
      icon: <Facebook />,
    },
    {
      title: 'Instagram',
      link: `https://www.instagram.com/${t('footer.social.instagram')}`,
      icon: <Instagram />,
    },
    {
      title: 'X (Twitter)',
      link: `https://twitter.com/${t('footer.social.twitter')}`,
      icon: <XIcon />,
    },
    {
      title: 'LinkedIn',
      link: 'https://www.linkedin.com/company/4837120',
      icon: <LinkedIn />,
    },
    {
      title: 'YouTube',
      link: 'https://www.youtube.com/channel/UC9PL_2kIEisstpgJZ2GlRfg',
      icon: <YouTube />,
    },
    {
      title: 'Google review',
      link: 'https://g.page/r/CQBke-lX1nYcEAg/review',
      icon: <Google />,
    },
  ];

  return (
    <Root className="site__footer">
      <Box bgcolor="primary.main">
        <PageSectionContainer sx={{ pb: 0 }}>
          <Grid container columnSpacing={2} rowSpacing={0}>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{
                '& a': {
                  display: 'block',
                  mb: 1.5,
                },
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                {t('footer.shortcut.title')}
              </Typography>

              <ul className="no-list-style">
                {shortcuts.map((article, index) => (
                  <li key={`shortcut-${index}`}>
                    <Link component={RouterLink} to={article.path}>
                      {article.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{
                '& a': {
                  display: 'block',
                  mb: 1.5,
                },
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                {t('footer.contact.title')}
              </Typography>
              <ul className="no-list-style">
                <li key="shortcut-contact-1">
                  <Link component={RouterLink} to={`/articles/${stringToSlug(t('footer.contact.title'), slugOptions)}`}>
                    {t('footer.contact.link')}
                  </Link>
                </li>
              </ul>
            </Grid>

            <Grid item xs={12}>
              <Divider variant="middle" sx={{ borderColor: 'primary.dark', my: 1, mx: 0 }} />
            </Grid>
            <Grid
              item
              xs={12}
              container
              sx={{
                justifyContent: onMobileAndTablet ? 'flex-start' : 'flex-end',
                pb: 2,
              }}
            >
              {socials.map((social, index) => (
                <IconButton
                  aria-label={social.title}
                  key={`social-${index}`}
                  color="inherit"
                  size="small"
                  component={Link}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    p: 1,
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
              {i18n.language === 'de' && (
                <IconButton
                  aria-label="Xing"
                  color="inherit"
                  size="small"
                  component={Link}
                  href="https://www.xing.com/pages/tovergmbh"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    p: 1,
                  }}
                >
                  <XingIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </PageSectionContainer>
      </Box>

      <Container
        maxWidth={false}
        sx={{
          backgroundColor: 'secondary.main',
          color: 'common.white',
          p: '1rem 0 2rem',
        }}
      >
        <Grid
          container
          rowSpacing={2}
          sx={{
            alignItems: 'center',
            pt: '1rem',
            justifyContent: onMobileAndTablet ? 'center' : 'space-between',
          }}
        >
          <Grid item xs={12} sm={10} container rowSpacing={2}>
            <Grid
              item
              container
              sx={{
                justifyContent: onMobileAndTablet ? 'center' : undefined,
                gap: 2,
              }}
            >
              <Grid item>
                <Typography>{t('footer.copyright', { year: currentYear })}</Typography>
              </Grid>
              <Grid item>
                <Link color="inherit" component={RouterLink} to="/privacy">
                  {t('footer.policy')}
                </Link>
              </Grid>
              <Grid item>
                <Link color="inherit" component={RouterLink} to="/eula">
                  {t('footer.eula')}
                </Link>
              </Grid>
              <Grid item>
                <Link color="inherit" component={RouterLink} to="/processing-conditions">
                  {t('footer.processing-conditions')}
                </Link>
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={clsx(onMobileAndTablet && 'mb-2')}>
            <Link color="inherit" component={RouterLink} to="/">
              <SvgIcon
                viewBox="0 0 250 65"
                sx={{
                  height: 40,
                  width: 'auto',
                }}
              >
                <path d="M239.691 16.657a6.956 6.956 0 00-3.938-3.939 7.564 7.564 0 00-9.68 3.546c-.113.226-.226.395-.282.506a2.857 2.857 0 01-2.7 1.969 2.925 2.925 0 01-1.407-.395v-5.346a.986.986 0 00-.956-.956h-11.486a.986.986 0 00-.956.956v48.849a.986.986 0 00.956.956h11.486a.986.986 0 00.956-.956v-36.41a2.53 2.53 0 012.759-1.802 2.11 2.11 0 01.556.056 4.18 4.18 0 012.476 1.237 7.496 7.496 0 0012.213-8.273zm-89.144-2.759c.055-.168.168-.337.226-.506a.84.84 0 00-.732-1.35h-11.762c-.845.055-1.127.845-1.406 1.406-1.577 3.264-3.209 6.584-4.784 9.793l-.055.055c-.395.788-.732 1.577-1.127 2.364-.674 1.464-1.406 2.87-2.082 4.333-1.35 2.702-2.814 5.347-4.333 8.274a.319.319 0 01-.556 0c-.45-.845-.732-1.35-1.013-1.914-3.715-7.595-7.374-15.308-11.144-22.905-.281-.556-.505-1.406-1.463-1.406-.845 0-7.316.055-11.762 0a.926.926 0 00-.845 1.35c.055.169.168.338.224.506 7.316 15.14 14.636 30.334 21.95 45.474l1.913 4.109a.986.986 0 00.845.556h3.151a.986.986 0 00.845-.556l1.406-2.927c.224-.45.395-.845.619-1.297v.113l6.415-13.339c1.297-2.7 2.594-5.346 3.89-8.047v.055c3.87-8.044 7.697-16.091 11.58-24.14zM38.439 10.41a7.566 7.566 0 00-9.68 3.546 2.025 2.025 0 01-.281.505 2.912 2.912 0 01-2.701 1.97 2.816 2.816 0 01-1.52-.45c0-3.135-.05-11.351 0-13.958 0-1.35.056-1.914-1.852-1.914h-9.91c-2.026 0-1.913.506-1.852 1.914.113 3.096 0 6.247.055 9.343 0 1.182-.337 1.565-1.576 1.576-3.681.04-5.408.135-6.753.113-2.594-.044-2.364.956-2.364 2.533v6.247c0 2.082.395 2.477 2.533 2.532H9.46a1.128 1.128 0 011.126 1.127v1.068c.056 7.092-.055 14.182.282 21.274a15.163 15.163 0 007.484 13.112c6.753 3.89 13.845 3.265 21.16 2.027.845-.168.845-.676.845-.956.056-3.15.056-6.078.056-9.174a.986.986 0 00-.956-.956h-6.816c-5.743-.055-8.385-2.7-8.385-8.385 0-6.36.113-13.788.169-20.147a3.28 3.28 0 01.067-.681 2.664 2.664 0 012.634-1.345 2.112 2.112 0 01.556.055 4.18 4.18 0 012.477 1.238 7.525 7.525 0 0012.212-8.337 6.706 6.706 0 00-3.933-3.877zm33.43 42.886a15.252 15.252 0 1115.252-15.252A15.228 15.228 0 0171.87 53.295zm0-42.096a26.846 26.846 0 1026.846 26.846A26.84 26.84 0 0071.869 11.2zm110.813 21.331c-4.67-.055-9.399-.055-14.07.056-1.298.056-2.478.056-3.77.056a.984.984 0 01-.957-1.182 12.93 12.93 0 0110.243-9.625c4.952-.505 9.117.732 11.93 5.178.17.282.338.62.507.9 1.86 4.164 1.182 4.671-3.883 4.617zM170.243 11.65a25.43 25.43 0 00-20.147 24.424 24.79 24.79 0 0010.525 22.455 29.07 29.07 0 0024.538 4.56 29.378 29.378 0 0012.267-6.135 1.028 1.028 0 00.226-1.237c-1.576-2.533-3.038-4.952-4.559-7.43a.94.94 0 00-1.35-.28.293.293 0 01-.224.112 22.231 22.231 0 01-18.065 3.705 12.638 12.638 0 01-9.398-8.78.963.963 0 01.9-1.181h.556c9.005-.056 18.009-.056 27.014-.056 1.97 0 3.94.056 5.91-.055a2.105 2.105 0 001.577-1.127 28.999 28.999 0 00-.226-12.438c-3.374-13.05-16.374-19.858-29.544-16.538z" />
              </SvgIcon>
            </Link>
          </Grid>
        </Grid>
      </Container>
    </Root>
  );
};
